import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";

import { addBucket, cloneBuckets } from "../services/seasonSequenceApi";

// Canonical default Franchise Calendar buckets, mapped up-front so a scaffolded
// calendar is immediately correct instead of showing "Needs mapping." Other is a
// standard bucket (maps_to_trackingseason = 0) alongside the four tracking seasons.
const DEFAULT_BUCKETS = [
  { sequence_name: "Spring", maps_to_trackingseason: 1 },
  { sequence_name: "Summer", maps_to_trackingseason: 2 },
  { sequence_name: "Fall", maps_to_trackingseason: 3 },
  { sequence_name: "Winter", maps_to_trackingseason: 4 },
  { sequence_name: "Other", maps_to_trackingseason: 0 },
];

// Preselect the most recent configured year *before* the target, if one
// exists (e.g. target 2027, source defaults to 2026 over any later year).
// Falls back to the most recent configured year overall if nothing precedes
// the target (e.g. target 2020 but only 2024 is configured).
function getDefaultSourceYear(sourceYearOptions, targetYear) {
  if (sourceYearOptions.length === 0) return "";

  const priorYears = sourceYearOptions.filter((year) => year < targetYear);
  if (priorYears.length > 0) {
    return Math.max(...priorYears);
  }

  return Math.max(...sourceYearOptions);
}

export default function SeasonSequenceEmptyState(props) {
  const comCode = props.comCode;
  const trackingYear = props.trackingYear;
  const trackingYears = props.trackingYears || [];
  const onConfigured = props.onConfigured;

  const sourceYearOptions = trackingYears
    .filter((y) => y.has_buckets && y.trackingyear !== trackingYear)
    .map((y) => y.trackingyear);

  const [selectedSourceYear, setSelectedSourceYear] = useState(() =>
    getDefaultSourceYear(sourceYearOptions, trackingYear)
  );

  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState(null);

  const [creatingDefaults, setCreatingDefaults] = useState(false);
  const [defaultsError, setDefaultsError] = useState(null);

  const isBusy = cloning || creatingDefaults;

  const sourceYearMenuItems = sourceYearOptions.map((year) => (
    <MenuItem key={year} value={year}>
      {year}
    </MenuItem>
  ));

  const handleCloneClick = () => {
    setCloning(true);
    setCloneError(null);

    cloneBuckets({
      com_code: comCode,
      sourceTrackingYear: selectedSourceYear,
      targetTrackingYear: trackingYear,
    })
      .then(() => {
        onConfigured();
      })
      .catch((err) => {
        setCloneError(err.message);
      })
      .finally(() => {
        setCloning(false);
      });
  };

  // Sequential, not parallel: each addBucket relies on the server computing
  // MAX(existing)+1, so awaiting one at a time avoids relying on the
  // concurrent-add 409 collision handling and keeps a partial failure
  // legible (we know exactly which names were created before the one that
  // failed). onConfigured() still fires on failure so the partial state
  // (if any) is visible via a refetch rather than left ambiguous -- Clone
  // doesn't need this since it's a single atomic statement server-side.
  const handleStartWithDefaultsClick = async () => {
    setCreatingDefaults(true);
    setDefaultsError(null);

    try {
      for (const bucket of DEFAULT_BUCKETS) {
        await addBucket({ com_code: comCode, trackingyear: trackingYear, ...bucket });
      }
    } catch (err) {
      setDefaultsError(err.message);
    } finally {
      setCreatingDefaults(false);
      onConfigured();
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        No buckets configured for {trackingYear} yet
      </Typography>

      {sourceYearOptions.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Clone from an existing year
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mt: 1 }}>
            <FormControl sx={{ minWidth: 160 }} disabled={isBusy}>
              <InputLabel id="clone-source-year-label">Source year</InputLabel>
              <Select
                labelId="clone-source-year-label"
                label="Source year"
                value={selectedSourceYear}
                onChange={(e) => setSelectedSourceYear(e.target.value)}
              >
                {sourceYearMenuItems}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleCloneClick}
              disabled={isBusy || selectedSourceYear === ""}
            >
              Clone
            </Button>
          </Box>
          {cloneError && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
              {cloneError}
            </Typography>
          )}

          <Divider sx={{ my: 3 }} />
        </>
      )}

      <Typography variant="subtitle2">Start fresh</Typography>
      <Box sx={{ mt: 1 }}>
        <Button variant="outlined" onClick={handleStartWithDefaultsClick} disabled={isBusy}>
          Start with the default calendar (Spring, Summer, Fall, Winter, Other)
        </Button>
      </Box>
      {defaultsError && (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          {defaultsError}
        </Typography>
      )}
    </Paper>
  );
}
